// MF-67 — Standard list body for the journal lists: loading state, empty state, the card-style table
// (roomy rows, quiet hover) and the pagination footer inside the same card. Columns are declarative:
// [{ key, label, width, align, render(row) }].
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Typography
} from '@mui/material'
import { journalTableSx, tableCardStyle } from './tableStyles'

const ListTable = ({
  columns = [],
  rows = [],
  loading,
  emptyText = 'Nothing here yet',
  rowKey = '_id',
  pagination,
  minWidth = 720
}) => {
  if (loading) {
    return (
      <div className='flex justify-center' style={{ padding: '40px 0' }}>
        <CircularProgress />
      </div>
    )
  }
  if (!rows.length) {
    return (
      <div style={{ ...tableCardStyle, padding: 40 }} className='text-center'>
        <Typography style={{ color: 'rgb(100, 116, 139)' }}>{emptyText}</Typography>
      </div>
    )
  }
  return (
    <div style={tableCardStyle}>
      <div style={{ overflowX: 'auto' }}>
        <Table sx={journalTableSx} style={{ tableLayout: 'fixed', minWidth }}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={row[rowKey] ?? i}>
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align || 'left'} style={{ overflow: 'hidden' }}>
                    {c.render ? c.render(row) : (row[c.key] ?? '—')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {pagination ? (
        <TablePagination
          component='div'
          count={pagination.count}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, p) => pagination.onPageChange(p)}
          onRowsPerPageChange={(e) => pagination.onRowsPerPageChange(parseInt(e.target.value, 10))}
          style={{ borderTop: '1px solid rgb(241, 245, 249)' }}
        />
      ) : null}
    </div>
  )
}

// Two-line cell: a primary line and a muted secondary line (both truncate).
export const Stacked = ({ primary, secondary }) => (
  <div className='flex flex-col' style={{ gap: 2, minWidth: 0 }}>
    <span
      style={{
        fontWeight: 500,
        color: 'rgb(15, 23, 42)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
      title={typeof primary === 'string' ? primary : undefined}
    >
      {primary || '—'}
    </span>
    {secondary ? (
      <span
        style={{
          fontSize: 12,
          color: 'rgb(100, 116, 139)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={typeof secondary === 'string' ? secondary : undefined}
      >
        {secondary}
      </span>
    ) : null}
  </div>
)

export const Muted = ({ children }) => <span style={{ color: 'rgb(148, 163, 184)' }}>{children}</span>

export default ListTable
